import { Link } from 'react-router-dom';
import { useLocale, useT, withLocale } from '../i18n';
import { useApiData } from '../api/useApiData';
import { contentApi } from '../api/content';
import AsyncSection from '../components/shared/AsyncSection';
import MethodologyOrbit from '../components/shared/MethodologyOrbit';

const METHODOLOGY_STAGES = [
  { code: 'assess', stage: 'Assess' },
  { code: 'map', stage: 'Map' },
  { code: 'prioritize', stage: 'Prioritize' },
  { code: 'architect', stage: 'Architect' },
  { code: 'build', stage: 'Build' },
  { code: 'automate', stage: 'Automate' },
  { code: 'govern', stage: 'Govern' },
  { code: 'measure', stage: 'Measure' },
  { code: 'optimize', stage: 'Optimize' },
  { code: 'scale', stage: 'Scale' },
];

const ENTITIES = [
  { name: 'CYRKIL Consulting', fn: 'We determine what must change and why', rev: 'Advisory fees · assessments · retainers' },
  { name: 'CYRKIL Engineering', fn: 'We build, deliver, and integrate', rev: 'Projects · time and materials · outcome' },
  { name: 'CYRKIL Intelligence Platform', fn: 'We supply the technology it runs on', rev: 'Subscription · licence · usage' },
  { name: 'CYRKIL AI Lab', fn: 'We invent what comes next', rev: 'Contract research · IP · licensing · spin-off' },
];

const VALUE_CHAIN = ['Process', 'Intelligence', 'Automation', 'AI Workforce', 'Intelligent Enterprise'];

export default function Home() {
  const locale = useLocale();
  const t = useT();
  const solutionsState = useApiData(() => contentApi.solutions(locale), [locale]);

  return (
    <>
      <section className="hero">
        <div className="hero-orbit-wrap">
          <MethodologyOrbit stages={METHODOLOGY_STAGES} activeCode="assess" maxWidth={780} />
        </div>
        <div className="container">
          <div className="hero-inner">
            <span className="eyebrow reveal is-visible">{t('home.hero_eyebrow')}</span>
            <h1 className="display-1 reveal is-visible">{t('home.hero_headline')}</h1>
            <p className="hero-lead body-lg reveal is-visible">{t('home.hero_lead')}</p>
            <div className="hero-cta-row reveal is-visible">
              <Link className="btn btn-primary btn-lg" to={withLocale('/contact', locale) + '?purpose=start_transformation'}>
                {t('nav.start_transformation')}
              </Link>
              <a className="btn btn-secondary btn-lg" href="#problem">
                {t('common.explore_cyrkil')}
              </a>
            </div>
            <p className="hero-lisa-hint reveal is-visible">
              {t('common.not_sure')} <button type="button" data-open-lisa>{t('common.ask_lisa')}</button>
            </p>
          </div>
        </div>
      </section>

      <section className="section" id="problem">
        <div className="container">
          <div className="section-head reveal is-visible">
            <span className="eyebrow">{t('home.problem_eyebrow')}</span>
            <h2>{t('home.problem_headline')}</h2>
          </div>
          <div className="card-grid">
            {(['1', '2', '3'] as const).map((n, i) => (
              <div className="card reveal is-visible" key={n}>
                <div className="card-icon">{['✳', '⚙', '◈'][i]}</div>
                <h3>{t(`home.problem_${n}_title`)}</h3>
                <p className="body-sm">{t(`home.problem_${n}_body`)}</p>
                <Link className="card-link" to={withLocale('/solutions/ai-software-development', locale)}>
                  →
                </Link>
              </div>
            ))}
          </div>
          <div className="section-foot">
            <a className="btn btn-secondary" href="#engine">
              {t('home.see_fix')}
            </a>
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container">
          <div className="section-head center reveal is-visible">
            <span className="eyebrow">{t('home.chain_eyebrow')}</span>
            <h2>{t('home.chain_headline')}</h2>
          </div>
          <div className="stepper reveal is-visible">
            {VALUE_CHAIN.map((label, i) => (
              <div className={`step-item ${i === 0 ? 'is-active' : ''}`} key={label}>
                <div className="num">{String(i + 1).padStart(2, '0')}</div>
                <h3>{label}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" id="engine">
        <div className="container">
          <div className="section-head center reveal is-visible">
            <span className="eyebrow">{t('home.engine_eyebrow')}</span>
            <h2>{t('home.engine_headline')}</h2>
            <p>{t('home.engine_quote')}</p>
          </div>
          <div className="reveal is-visible" style={{ display: 'flex', justifyContent: 'center' }}>
            <MethodologyOrbit stages={METHODOLOGY_STAGES} activeCode="assess" maxWidth={620} />
          </div>
          <div className="section-foot">
            <Link className="btn btn-secondary" to={withLocale('/methodology', locale)}>
              {t('home.engine_cta')}
            </Link>
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container">
          <div className="section-head center reveal is-visible">
            <span className="eyebrow">{t('home.entities_eyebrow')}</span>
            <h2>{t('home.entities_headline')}</h2>
            <p>{t('home.entities_body')}</p>
          </div>
          <div className="entities-grid">
            {ENTITIES.map((e) => (
              <div className="card entity-card reveal is-visible" key={e.name}>
                <h3>{e.name}</h3>
                <p className="fn">{e.fn}</p>
                <p className="rev">{e.rev}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head reveal is-visible">
            <span className="eyebrow">{t('home.solutions_eyebrow')}</span>
            <h2>{t('home.solutions_headline')}</h2>
            <p>{t('home.solutions_body')}</p>
          </div>
          <AsyncSection
            state={solutionsState}
            render={(solutions) => (
              <div className="card-grid">
                {solutions.map((s) => (
                  <Link className="card reveal is-visible" to={withLocale(`/solutions/${s.slug}`, locale)} key={s.code}>
                    <span className="badge badge-core">{s.classification}</span>
                    <h3 style={{ marginTop: 12 }}>{s.name}</h3>
                    <p className="body-sm">{s.problem}</p>
                    <span className="card-link">{s.code} →</span>
                  </Link>
                ))}
              </div>
            )}
          />
          <div className="section-foot">
            <Link className="btn btn-secondary" to={withLocale('/solutions', locale)}>
              {t('nav.view_all_12')}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
